import { CloudOff, RefreshCw } from 'lucide-react'
import { useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { reportApiSuccess, useApiHealth } from '@/lib/apiHealth'
import { reconnectPlan } from './reconnectPlan'
import styles from './ApiStatusBanner.module.scss'

/** While the API is down, failed queries are retried this often. */
const RECONNECT_INTERVAL_MS = 20_000

/**
 * Global strip shown while the API can't be reached (network, tunnel or
 * backend down — ``lib/apiHealth``). Every 20 s on its own and on "Tentar
 * agora" it probes the API (``reconnectPlan``: the failed queries, else the
 * active ones), and goes away on the first successful request. With no
 * query to probe, only the click hides it, never the timer. AppLayout mounts
 * it above the routed page.
 */
export function ApiStatusBanner() {
  const { down } = useApiHealth()
  const queryClient = useQueryClient()

  const reconnect = useCallback(
    (manual: boolean) => {
      const cache = queryClient.getQueryCache()
      const plan = reconnectPlan(
        cache.findAll({ type: 'active', predicate: (query) => query.state.status === 'error' }).length,
        cache.findAll({ type: 'active' }).length,
        manual,
      )
      if (plan === 'failed') {
        void queryClient.refetchQueries({
          type: 'active',
          predicate: (query) => query.state.status === 'error',
        })
      } else if (plan === 'active') {
        void queryClient.refetchQueries({ type: 'active' })
      } else if (plan === 'clear') {
        reportApiSuccess()
      }
    },
    [queryClient],
  )

  useEffect(() => {
    if (!down) return
    const id = window.setInterval(() => {
      reconnect(false)
    }, RECONNECT_INTERVAL_MS)
    return () => {
      window.clearInterval(id)
    }
  }, [down, reconnect])

  if (!down) return null

  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <CloudOff aria-hidden className={styles.icon} />
      <span className={styles.text}>
        Servidor do Prism indisponível — pode ser manutenção. Tentando reconectar…
      </span>
      <button
        type="button"
        className={styles.retry}
        onClick={() => {
          reconnect(true)
        }}
      >
        <RefreshCw aria-hidden />
        Tentar agora
      </button>
    </div>
  )
}
